import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";

import {BaseListService, BatchRequest, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {Lookup} from "framework/services/utils.service";

@Injectable()
export class ComplianceControlsListService extends BaseListService<Models.ComplianceControlListRequest, BatchRequest, Models.ComplianceControlDetail>
{
    private static readonly BATCH_LIMIT: number = 1000;

    private readonly m_controlsByKey: Lookup<Models.ComplianceControlDetail[]>          = {};
    private readonly m_fetchingByKey: Lookup<Promise<Models.ComplianceControlDetail[]>> = {};
    private readonly m_controlsByUri: Lookup<Models.ComplianceControlDetail>            = {};

    private readonly m_controlsByPolicyKey: Lookup<Models.ComplianceControlDetail[]>          = {};
    private readonly m_fetchingByPolicyKey: Lookup<Promise<Models.ComplianceControlDetail[]>> = {};

    constructor(inj: Injector)
    {
        super(inj, Models.ComplianceControlDetail);
    }

    @ReportError
    public list(listReq: Models.ComplianceControlListRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.compliance.listComplianceControls(listReq) as Promise<Models.ListResponse>;
    }

    @ReportError
    protected async getBatchResponseImpl(req: BatchRequest): Promise<BatchResponse<Models.ComplianceControlDetail>>
    {
        const controls                                       = await this.domain.apis.compliance.batchComplianceControls(
            Models.ControlBatchRequest.newInstance({
                                                       compliance_control_ids: this.getSysIds(req)
                                                   })
        );
        const lookup: Lookup<Models.ComplianceControlDetail> = {};
        for (const ctrl of controls || [])
        {
            lookup[ctrl.compliance_control_id] = ctrl;
        }
        return {results: lookup};
    }

    protected createBatchRequest(listReq: Models.ComplianceControlListRequest,
                                 ids: string[]): BatchRequest
    {
        return {ids};
    }

    public static compareByControlUri(a: Models.ComplianceControlDetail,
                                      b: Models.ComplianceControlDetail): number
    {
        return (a.compliance_control_uri || "").localeCompare(b.compliance_control_uri || "", undefined, {
            numeric    : true,
            sensitivity: "base"
        });
    }

    public async getAllForStandard(tenantId: string,
                                   standardId: string,
                                   natures?: Models.ControlNature[]): Promise<Models.ComplianceControlDetail[]>
    {
        const cacheKey = ComplianceControlsListService.buildStandardCacheKey(tenantId, standardId, natures);

        if (this.m_controlsByKey[cacheKey])
        {
            return this.m_controlsByKey[cacheKey];
        }

        if (!this.m_fetchingByKey[cacheKey])
        {
            this.m_fetchingByKey[cacheKey] = (async () =>
            {
                try
                {
                    const request = Models.ComplianceControlListRequest.newInstance({
                                                                                        list_context: Models.ListContext.newInstance({
                                                                                                                                         batch_size  : 500,
                                                                                                                                         batch_offset: 0
                                                                                                                                     }),
                                                                                        filters     : Models.ComplianceControlFilter.newInstance({
                                                                                                                                                     tenant_id             : tenantId,
                                                                                                                                                     compliance_standard_id: standardId,
                                                                                                                                                     query                 : natures?.length
                                                                                                                                                         ? Models.ComplianceControlNatureQueryOperator.newInstance(
                                                                                                                                                             {natures})
                                                                                                                                                         : undefined
                                                                                                                                                 })
                                                                                    });

                    const listResponse = await this.list(request);
                    if (listResponse?.results?.length > 0)
                    {
                        const ids                      = await this.listAll(request);
                        const controls                 = await this.batchAll(ids, ComplianceControlsListService.BATCH_LIMIT);
                        this.m_controlsByKey[cacheKey] = controls.sort(ComplianceControlsListService.compareByControlUri);
                    }
                    else
                    {
                        this.m_controlsByKey[cacheKey] = [];
                    }
                }
                finally
                {
                    delete this.m_fetchingByKey[cacheKey];
                }

                return this.m_controlsByKey[cacheKey];
            })();
        }

        return this.m_fetchingByKey[cacheKey];
    }

    public async getForPolicy(tenantId: string,
                              standardId: string,
                              policyUri: string): Promise<Models.ComplianceControlDetail[]>
    {
        const cacheKey = ComplianceControlsListService.buildPolicyCacheKey(tenantId, standardId, policyUri);

        if (this.m_controlsByPolicyKey[cacheKey])
        {
            return this.m_controlsByPolicyKey[cacheKey];
        }

        if (!this.m_fetchingByPolicyKey[cacheKey])
        {
            this.m_fetchingByPolicyKey[cacheKey] = (async () =>
            {
                try
                {
                    const request = Models.ComplianceControlListRequest.newInstance({
                                                                                        list_context: Models.ListContext.newInstance({
                                                                                                                                         batch_size  : 500,
                                                                                                                                         batch_offset: 0
                                                                                                                                     }),
                                                                                        filters     : Models.ComplianceControlFilter.newInstance({
                                                                                                                                                     tenant_id             : tenantId,
                                                                                                                                                     compliance_standard_id: standardId,
                                                                                                                                                     query                 : Models.ComplianceControlPolicyUris.newInstance(
                                                                                                                                                         {policy_uris: [policyUri]})
                                                                                                                                                 })
                                                                                    });

                    const ids                            = await this.listAll(request);
                    const controls                       = await this.batchAll(ids, ComplianceControlsListService.BATCH_LIMIT);
                    this.m_controlsByPolicyKey[cacheKey] = controls.sort(ComplianceControlsListService.compareByControlUri);
                }
                finally
                {
                    delete this.m_fetchingByPolicyKey[cacheKey];
                }

                return this.m_controlsByPolicyKey[cacheKey];
            })();
        }

        return this.m_fetchingByPolicyKey[cacheKey];
    }

    public async getAllForControlUris(tenantId: string,
                                      controlUris: readonly string[]): Promise<Models.ComplianceControlDetail[]>
    {
        const wanted = Array.from(new Set((controlUris || []).filter(Boolean)));
        if (!wanted.length) return [];

        const missing = wanted.filter((uri) => !this.m_controlsByUri[ComplianceControlsListService.uriCacheKey(tenantId, uri)]);

        for (let offset = 0; offset < missing.length; offset += ComplianceControlsListService.BATCH_LIMIT)
        {
            const chunk = missing.slice(offset, offset + ComplianceControlsListService.BATCH_LIMIT);

            const controls = await this.domain.apis.compliance.batchComplianceControls(
                Models.ControlBatchRequest.newInstance({
                                                           compliance_control_uris: chunk
                                                       })
            );

            for (const control of controls || [])
            {
                if (control?.compliance_control_uri)
                {
                    this.m_controlsByUri[ComplianceControlsListService.uriCacheKey(tenantId, control.compliance_control_uri)] = control;
                }
            }
        }

        return wanted.map((uri) => this.m_controlsByUri[ComplianceControlsListService.uriCacheKey(tenantId, uri)])
                     .filter(Boolean)
                     .sort(ComplianceControlsListService.compareByControlUri);
    }

    public resetStandardCache(tenantId: string,
                              standardId: string): void
    {
        const prefix = `${tenantId}|||${standardId}`;
        for (const key of Object.keys(this.m_controlsByKey))
        {
            if (key === prefix || key.startsWith(`${prefix}|||`))
            {
                this.m_controlsByKey[key] = null;
            }
        }

        for (const key of Object.keys(this.m_controlsByPolicyKey))
        {
            if (key === prefix || key.startsWith(`${prefix}|||`))
            {
                this.m_controlsByPolicyKey[key] = null;
            }
        }

        this.clearUriCache(tenantId);
    }

    public resetTenantCache(tenantId: string): void
    {
        const prefix = `${tenantId}|||`;
        for (const key of Object.keys(this.m_controlsByKey))
        {
            if (key.startsWith(prefix))
            {
                this.m_controlsByKey[key] = null;
            }
        }

        for (const key of Object.keys(this.m_controlsByPolicyKey))
        {
            if (key.startsWith(prefix))
            {
                this.m_controlsByPolicyKey[key] = null;
            }
        }

        this.clearUriCache(tenantId);
    }

    private clearUriCache(tenantId: string): void
    {
        const prefix = `${tenantId}|||`;
        for (const key of Object.keys(this.m_controlsByUri))
        {
            if (key.startsWith(prefix))
            {
                delete this.m_controlsByUri[key];
            }
        }
    }

    private static uriCacheKey(tenantId: string,
                               controlUri: string): string
    {
        return `${tenantId}|||${controlUri}`;
    }

    private static buildPolicyCacheKey(tenantId: string,
                                       standardId: string,
                                       policyUri: string): string
    {
        return `${tenantId}|||${standardId}|||policy:${policyUri}`;
    }

    private static buildStandardCacheKey(tenantId: string,
                                         standardId: string,
                                         natures?: Models.ControlNature[]): string
    {
        const base = `${tenantId}|||${standardId}`;
        if (!natures?.length) return base;

        return `${base}|||${Array.from(new Set(natures))
                                 .sort()
                                 .join(",")}`;
    }
}